import { STEPS, TEMPLATE } from './constants';
import { ICourseRoles } from './ICourseRoles';
import { IMapping } from './IResultsSource';
import { IUserInfo } from './IUserInfo';
import { formatDate, formatFileDate, traverseObject } from './parse';

export class WorkflowTextService {

  public static getModuleName(moduleId?: string): string {
    switch (moduleId) {
      case STEPS.STEP1:
        return "Step 1";
      case STEPS.STEP2:
        return "Step 2";
      case STEPS.STEP3:
        return "Step 3";
      case STEPS.REVIEW:
        return "External Course Review";
      case STEPS.COMPLETED:
        return "Completed";
      default:
        return '';
    };
  }

  public static isExpertVisible(roles: ICourseRoles) {
    return roles.isStaff || roles.isAdmin || roles.isExternalExpert
  }

  public static formatExpertLink(expert: IUserInfo | undefined, roles: ICourseRoles): string {
    return expert && WorkflowTextService.isExpertVisible(roles) ? `<a href='mailto:${expert.email}' style='white-space: nowrap; text-decoration: underline' title='${expert.email}'>${expert.name}</a>` : ''
  }

  public static getTemplateDescription(templateId?: string) {
    switch (templateId) {

      case TEMPLATE.STEP1_JSON: return "Pre-self assessment for EIT Label application";
      case TEMPLATE.STEP2_JSON: return "Course Development and Implementation";
      case TEMPLATE.STEP3_JSON: return "Outcome and Final Assessment";
      case TEMPLATE.REVIEW_JSON: return "Course Review by External Reviewer";

      case TEMPLATE.V2_1_STEP1_JSON: return "Pre-self assessment for EIT Label application V2";
      case TEMPLATE.V2_2_STEP1_WF_JSON: return "Workflow for Pre-self assessment for EIT Label application V2";
      case TEMPLATE.V2_3_REVIEW_JSON: return "Course Review by External Reviewer V2";
      case TEMPLATE.V2_7_COMPLETED_JSON: return "Small Changes to the Course V2";
      case TEMPLATE.V2_4_REVIEW_WF_JSON: return "Workflowfor Course Review by External Reviewer V2";
      case TEMPLATE.V2_5_STEP2_JSON: return "Outcome and Final Assessment V2";
      case TEMPLATE.V2_6_STEP2_WF_JSON: return "Workflow for Outcome and Final Assessment V2";
      case TEMPLATE.V2_2_STEP1_FEEDBACK_TEMPLATE: return "Feedback for Pre-self assessment for EIT Label application V2";
      case TEMPLATE.V2_4_REVIEW_FEEDBACK_TEMPLATE: return "Feedback for Course Review by External Reviewer V2";
      case TEMPLATE.V2_6_STEP2_FEEDBACK_TEMPLATE: return "Feedback for Outcome and Final Assessment V2";

      case TEMPLATE.EMAIL_LAYOUT: return "The common template for the the email notification.";
      case TEMPLATE.PROVIDER_ACCESS: return "Notifies the course provider that they have been granted access to the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COURSE_CREATED: return "Notifies the course provider that a new course has been created in the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COURSE_SUBMITTED: return "Notifies the course provider that a course has been submitted for review."
      case TEMPLATE.PROVIDER_COURSE_APPROVED: return "Notifies the course provider that a course has been approved."
      case TEMPLATE.PROVIDER_COURSE_REJECTED: return "Notifies the course provider that a course has been rejected."
      case TEMPLATE.PROVIDER_STEP_SUBMITTED: return "Notifies the course provider that a step has been submitted for review."
      case TEMPLATE.PROVIDER_STEP_APPROVED: return "Notifies the course provider that a step has been approved."
      case TEMPLATE.PROVIDER_STEP_REJECTED: return "Notifies the course provider that a step has been rejected."
      case TEMPLATE.REVIEWER_COURSE_ACCESS: return "Notifies the reviewer that they have been granted access to the EIT UM Label application system."
      case TEMPLATE.REVIEWER_SUBMITTED: return "Notifies the reviewer that a course has been submitted for review."
      case TEMPLATE.REVIEWER_ACCESS: return "Notifies the reviewer that they have been granted access to the EIT UM Label application system."
      case TEMPLATE.STAFF_STEP_SUBMITTED: return "Notifies the EIT UM staff that a step has been submitted for review."
      case TEMPLATE.STAFF_COMMENT_CREATED: return "Notifies the EIT UM staff that a comment has been created in the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COMMENT_CLOSED: return "Notifies the course provider that a comment has been closed in the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COMMENT_CREATED: return "Notifies the course provider that a comment has been created in the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COMMENT_DELETED: return "Notifies the course provider that a comment has been deleted in the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COMMENT_REOPENED: return "Notifies the course provider that a comment has been reopened in the EIT UM Label application system."
      case TEMPLATE.PROVIDER_COMMENT_REPLY: return "Notifies the course provider that a comment has been replied in the EIT UM Label application system."
      case TEMPLATE.STAFF_COURSE_REMINDER: return "Notifies the EIT UM staff that a course has been created in the EIT UM Label application system and is waiting for review.";

      case TEMPLATE.V2_EMAIL_LAYOUT: return "The common template for the the email notification.";
      case TEMPLATE.V2_PROVIDER_ACCESS: return "Notifies the course provider that they have been granted access to the EIT UM Label application system."
      case TEMPLATE.V2_PROVIDER_COURSE_CREATED: return "Notifies the course provider that a new course has been created in the EIT UM Label application system."
      case TEMPLATE.V2_PROVIDER_ASSIGNED: return "Notifies that a user hast been associated with a course provider."
      case TEMPLATE.V2_PROVIDER_COURSE_ASSIGNED: return "Notifies the course provider that a course has been associated with him/her in the EIT UM Label application system."
      case TEMPLATE.V2_PROVIDER_COURSE_APPROVED_GOOD: return "Notifies the course provider that a course has been approved (Good)."
      case TEMPLATE.V2_PROVIDER_COURSE_APPROVED_SATISFACTORY: return "Notifies the course provider that a course has been approved (Satisfactory)."
      case TEMPLATE.V2_PROVIDER_COURSE_REJECTED: return "Notifies the course provider that a course has been rejected."
      case TEMPLATE.V2_PROVIDER_STEP_SUBMITTED: return "Notifies the course provider that a step has been submitted for review."
      case TEMPLATE.V2_PROVIDER_STEP_APPROVED: return "Notifies the course provider that a step has been approved."
      case TEMPLATE.V2_PROVIDER_STEP_REJECTED: return "Notifies the course provider that a step has been rejected."
      case TEMPLATE.V2_REVIEWER_COURSE_ACCESS: return "Notifies the reviewer that they have been granted access to the EIT UM Label application system."
      case TEMPLATE.V2_REVIEWER_SUBMITTED: return "Notifies the reviewer that a course has been submitted for review."
      case TEMPLATE.V2_REVIEWER_ACCESS: return "Notifies the reviewer that they have been granted access to the EIT UM Label application system."
      case TEMPLATE.V2_STAFF_STEP_SUBMITTED: return "Notifies the EIT UM staff that a step has been submitted for review."
      
      case TEMPLATE.V2_COMMENT_CLOSED: return "Notifies that a comment has been closed in the EIT UM Label application system."
      case TEMPLATE.V2_COMMENT_CREATED: return "Notifies that a comment has been created in the EIT UM Label application system."
      case TEMPLATE.V2_COMMENT_DELETED: return "Notifies that a comment has been deleted in the EIT UM Label application system."
      case TEMPLATE.V2_COMMENT_REOPENED: return "Notifies that a comment has been reopened in the EIT UM Label application system."
      case TEMPLATE.V2_COMMENT_REPLY: return "Notifies that a comment has been replied in the EIT UM Label application system."

      case TEMPLATE.V2_PROVIDER_REVIEW_GOOD: return "Notifies the course provider that their course review has been marked as 'Good'."
      case TEMPLATE.V2_PROVIDER_REVIEW_SATISFACTORY: return "Notifies the course provider that their course review has been marked as 'Satisfactory'."
      case TEMPLATE.V2_PROVIDER_REVIEW_REJECTED: return "Notifies the course provider that their course review has been rejected."
      case TEMPLATE.V2_PROVIDER_REVIEW_REJECTED_COMPLETED: return "Notifies the course provider that their course review rejection has been completed."
      case TEMPLATE.V2_PROVIDER_REVIEW_REJECTED_OVERRULED: return "Notifies the course provider that their course review rejection has been overruled."
      case TEMPLATE.V2_PROVIDER_COURSE_APPROVED_SATISFACTORY_RETURNED: return "Notifies the course provider that their course has been approved as 'Satisfactory' and returned for review."
      
      case TEMPLATE.V2_PROVIDER_AMENDMENT: return "Confirms to the course provider that their course amendments have been successfully submitted for review."
      case TEMPLATE.V2_STAFF_AMENDMENT: return "Notifies staff members that a course provider has submitted amendments for review."
    };
  }

  public static getCourseBadgeColor = (courseStatus?: string) => {
    if (!courseStatus) {
      return 'gray';
    }
    if (courseStatus.includes('committee')) {
      return 'purple';
    }
    if (courseStatus.includes('expert')) {
      return 'cyan';
    }
    if (courseStatus.includes('rejected')) {
      return 'red';
    }
    if (courseStatus.includes('submitted')) {
      return 'blue';
    }
    if (courseStatus.includes('approved')) {
      return 'green';
    }
    if (courseStatus.includes('draft')) {
      return 'yellow';
    }
    return 'gray';
  }

// Helper function to extract step and status from CourseStep value (e.g., "STEP1 | draft")
  public static getStepAndStatus(courseStep: string) {
    const parts = courseStep.split(' | ');
    return {
      step: parts[0]?.trim() || courseStep,
      status: parts[1]?.trim() || ''
    };
  };

  public static compareCourseSteps(a: string, b: string): number {
    const aStepStatus = this.getStepAndStatus(a);
    const bStepStatus = this.getStepAndStatus(b);

    // Define the desired step order: STEP1 => REVIEW => STEP2 => STEP3 => COMPLETED
    const stepOrder = [STEPS.STEP1, STEPS.REVIEW, STEPS.STEP2, STEPS.STEP3, STEPS.COMPLETED];
    const stepIndexA = stepOrder.indexOf(aStepStatus.step as STEPS);
    const stepIndexB = stepOrder.indexOf(bStepStatus.step as STEPS);
    
    // Sort by step order first
    if (stepIndexA !== stepIndexB) {
      return stepIndexA - stepIndexB;
    }
    
    // If same step, sort by status in workflow order: draft => submitted => rejected => approved => expert => committee 
    const statusOrder = ['draft', 'submitted', 'rejected', 'approved', 'expert', 'committee'];
    const statusIndexA = statusOrder.indexOf(aStepStatus.status);
    const statusIndexB = statusOrder.indexOf(bStepStatus.status);
    
    if (statusIndexA !== -1 && statusIndexB !== -1) {
      return statusIndexA - statusIndexB;
    }
    
    // Fallback to alphabetical if status not in predefined order
    return aStepStatus.status.localeCompare(bStepStatus.status);
  }

  public static getDefaultMapping(template: { [key: string]: any }, excludes: RegExp[]): IMapping {
    const result: IMapping = {};
    traverseObject(template, obj => {
      if (obj.name && obj.type && obj.type !== 'panel' && obj.type !== 'page' && !excludes.some(prop => prop.test(obj.name))) {
        result[obj.name] = { dst: obj.name };
      }
    });
    return result;
  }

  private static getBooleanDefaultValue(value: any): boolean {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    } else {
      return value;
    }
  }

  public static getDefaultResults(template: { [key: string]: any }) {
    const result: any = {};
    traverseObject(template, obj => {
      if (obj.name && obj.type && obj.type !== 'panel' && obj.type !== 'page') {
        if (obj.defaultValue) {
          switch (obj.type) {
            case 'boolean':
              result[obj.name] = this.getBooleanDefaultValue(obj.defaultValue);
              break;
            default: {
              result[obj.name] = obj.defaultValue;
            }
          }
        }
      }
    });
    return result;
  }

  public static formatStatus(s: { moduleId: string, status: string }): string {
    return s.status ? `${s.moduleId} | ${s.status}` : s.moduleId;
  }

  public static parseStatus(v: string): { moduleId: string, status: string } | undefined {
    const match = v.match(/^go_(STEP1|REVIEW|STEP2|STEP3|COMPLETED)_?(approved|rejected|submitted|draft)?/);
    if (match) {
      return { moduleId: match[1], status: match[2] || '' };
    }
  }

  public static getPdfFileName(folderName: string, moduleId: string, isWorkflow?: boolean): string {
    const baseFileName = moduleId === STEPS.COMPLETED
      ? `${folderName}_${formatFileDate(new Date())}_amendment.pdf`
      : `${folderName}_${moduleId}${isWorkflow ? '_wf' : ''}.pdf`;
    
    // Add timestamp for workflow files to avoid overwriting
    if (isWorkflow) {
      return baseFileName.replace('.pdf', `_${new Date().toISOString().slice(0, 19).replace(/T/g, '_').replace(/:/g, '-')}.pdf`);
    }
    
    return baseFileName;
  }

  public static getPdfFileTitle(moduleId: string, isWorkflow?: boolean) {
    return moduleId === STEPS.COMPLETED
      ? `Course Amendment on ${formatDate(new Date())} PDF`
      : WorkflowTextService.getModuleName(moduleId) + (isWorkflow ? ' Workflow' : '') + ' PDF';
  }
}